import { useCallback, useEffect, useState } from 'react';
import { MIN_REASON } from '@urbanrats/ops-core';
import { Link } from '../components/Link.tsx';
import { fmtTime, useCtx } from '../lib.ts';

/**
 * Real money for one operator's sites (API mode). What is proven and what is not (measured now), which sites
 * run real money, the request to switch sites on (a second admin approves; the operator's licence and the
 * accepted open items are recorded), the immediate switch back to demo, and the history.
 */
interface Item {
  key: string;
  label: string;
  state: 'OK' | 'OPEN' | 'BLOCKING';
  detail: string;
}
interface Licence {
  jurisdiction: string;
  number: string;
  holder: string;
}
interface View {
  operatorId: string;
  kind: string;
  licence: Licence | null;
  newSitesMode: 'DEMO' | 'REAL';
  statement: string;
  readiness: Item[];
  sites: { brandId: string; name: string; siteId: string | null; currency: string; mode: 'DEMO' | 'REAL'; status: string; realMoneyCurrency: boolean }[];
  acceptances: { id: number; brandIds: string[]; newSites: boolean; requestId: string; maker: string; checker: string; licence: Licence; openItems: Item[]; createdAt: string; endedAt: string | null; endedBy: string | null; endReason: string | null }[];
}

const MARK = { OK: '✓', OPEN: '!', BLOCKING: '✕' } as const;

export function RealMoney({ operatorId, manage }: { operatorId: string; manage: boolean }) {
  const { backend } = useCtx();
  const server = backend.server!;
  const [v, setV] = useState<View | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pick, setPick] = useState<string[]>([]);
  const [lic, setLic] = useState<Licence>({ jurisdiction: '', number: '', holder: '' });
  const [newSites, setNewSites] = useState(false);
  const [accept, setAccept] = useState(false);
  const [reason, setReason] = useState('');
  const [offReason, setOffReason] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await server.get<View>(`/v1/admin/operators/${encodeURIComponent(operatorId)}/real-money`);
    if (!r.ok) return setErr(r.error);
    setV(r.value);
    setErr(null);
    if (r.value.licence) setLic((l) => (l.jurisdiction || l.number ? l : { ...r.value.licence!, holder: r.value.licence!.holder ?? '' }));
  }, [server, operatorId]);
  useEffect(() => {
    const t = setTimeout(() => void load(), 0);
    return () => clearTimeout(t);
  }, [load]);

  if (!v) return <p className="empty">{err ?? 'Checking real-money readiness…'}</p>;
  const real = v.sites.filter((s) => s.mode === 'REAL');
  const blocking = v.readiness.filter((i) => i.state === 'BLOCKING' && i.key !== 'licence' && i.key !== 'currency');
  const open = v.readiness.filter((i) => i.state === 'OPEN');
  const eligible = v.sites.filter((s) => s.mode === 'DEMO' && s.realMoneyCurrency && s.status === 'ACTIVE');
  const canAsk = manage && pick.length > 0 && blocking.length === 0 && !!lic.jurisdiction.trim() && !!lic.number.trim() && accept && reason.trim().length >= MIN_REASON && !busy;

  const ask = async () => {
    setBusy(true);
    const r = await server.post<{ requestId: string; summary: string }>('/v1/admin/change-requests', {
      kind: 'REAL_MONEY',
      target: operatorId,
      summary: 'real money',
      payload: { operatorId, brandIds: pick.join(','), newSites: String(newSites), licenceJurisdiction: lic.jurisdiction, licenceNumber: lic.number, licenceHolder: lic.holder, acceptRisks: v.statement },
      reason,
    });
    setBusy(false);
    if (!r.ok) return setMsg({ ok: false, text: r.error });
    setMsg({ ok: true, text: `Request ${r.value.requestId} sent: ${r.value.summary}. Another admin approves it under Audit → Approvals (they get a notification).` });
    setPick([]);
    setAccept(false);
    setReason('');
  };
  const off = async (ids: string[] | '*') => {
    setBusy(true);
    const r = await server.post<{ switchedOff: string[] }>(`/v1/admin/operators/${encodeURIComponent(operatorId)}/real-money/off`, { brandIds: ids, reason: offReason, newSites: ids === '*' });
    setBusy(false);
    if (!r.ok) return setMsg({ ok: false, text: r.error });
    setMsg({ ok: true, text: `Back to demo: ${r.value.switchedOff.join(', ') || 'nothing was on real money'}. New sessions play with play money; running sessions keep their mode.` });
    setOffReason('');
    await load();
  };

  return (
    <div className="op-pane rm" data-testid="real-money">
      <div className={`rm-head ${real.length ? 'on' : ''}`}>
        <strong>{real.length ? `Real money ON on ${real.length} of ${v.sites.length} site${v.sites.length === 1 ? '' : 's'}` : 'All sites run demo (play) money'}</strong>
        <span>
          Real money runs under the operator’s licence{v.licence ? ` (${v.licence.jurisdiction} · ${v.licence.number})` : ''}, on the operator’s own wallet. A session keeps the mode it was launched with. Fun play (demo sessions) stays play money on every site.
          {v.kind === 'AGGREGATOR' && ` New automatic sites start in ${v.newSitesMode === 'REAL' ? 'REAL money' : 'demo'}.`}
        </span>
      </div>

      <h4>Readiness — measured now</h4>
      <ul className="rm-list">
        {v.readiness.map((i) => (
          <li key={i.key} className={`rm-item rm-${i.state.toLowerCase()}`}>
            <span className="rm-mark" aria-label={i.state}>
              {MARK[i.state]}
            </span>
            <div>
              <strong>{i.label}</strong>
              <span className="rm-state">{i.state === 'OK' ? 'met' : i.state === 'OPEN' ? 'open — accepted as a risk when switching on' : 'required'}</span>
              <p>{i.detail}</p>
            </div>
          </li>
        ))}
      </ul>

      <h4>Sites</h4>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {manage && <th />}
              <th>Site</th>
              <th>Currency</th>
              <th>Money</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {v.sites.map((s) => {
              const can = s.mode === 'DEMO' && s.realMoneyCurrency && s.status === 'ACTIVE';
              return (
                <tr key={s.brandId}>
                  {manage && (
                    <td>
                      <input type="checkbox" aria-label={`Select ${s.brandId}`} disabled={!can} checked={pick.includes(s.brandId)} onChange={(e) => setPick(e.target.checked ? [...pick, s.brandId] : pick.filter((x) => x !== s.brandId))} />
                    </td>
                  )}
                  <td>
                    <strong>{s.name}</strong>
                    <div className="mono muted">{s.brandId}</div>
                  </td>
                  <td className="mono">
                    {s.currency}
                    {!s.realMoneyCurrency && <div className="muted small">play money only</div>}
                  </td>
                  <td>
                    <span className={`tag ${s.mode === 'REAL' ? 'money-real' : ''}`}>{s.mode === 'REAL' ? 'REAL money' : 'Demo'}</span>
                  </td>
                  <td>
                    <span className={`tag st-${s.status}`}>{s.status}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {msg && (
        <p role={msg.ok ? 'status' : 'alert'} className={msg.ok ? 'ok-text' : 'field-error'}>
          {msg.text}
        </p>
      )}

      {manage && (
        <section className="rm-form">
          <h4>Switch sites to real money</h4>
          {blocking.length > 0 ? (
            <p className="field-error">Not possible yet: {blocking.map((i) => i.label).join(', ')}.</p>
          ) : (
            <>
              <p className="note">
                Select the sites above ({eligible.length} can switch: demo, active, real-money currency EUR / USD / TRY). Another admin approves; on approval new sessions on these sites play for real money and prizes, drops and campaigns are switched off on them (Rewards page).
              </p>
              <div className="op-grid">
                <label className="field">
                  <span>Operator licence — jurisdiction</span>
                  <input value={lic.jurisdiction} onChange={(e) => setLic({ ...lic, jurisdiction: e.target.value })} placeholder="Curaçao" data-testid="rm-jurisdiction" />
                </label>
                <label className="field">
                  <span>Licence number</span>
                  <input value={lic.number} onChange={(e) => setLic({ ...lic, number: e.target.value })} placeholder="OGL/2024/…" data-testid="rm-number" />
                </label>
                <label className="field">
                  <span>Licence holder (company)</span>
                  <input value={lic.holder} onChange={(e) => setLic({ ...lic, holder: e.target.value })} placeholder="Operator company N.V." />
                </label>
              </div>
              {v.kind === 'AGGREGATOR' && (
                <label className="op-check">
                  <input type="checkbox" checked={newSites} onChange={(e) => setNewSites(e.target.checked)} /> New sites created automatically start in real money too (EUR / USD / TRY only)
                </label>
              )}
              {open.length > 0 && (
                <div className="rm-risks">
                  <strong>Open items you accept as risks ({open.length})</strong>
                  <ul>
                    {open.map((i) => (
                      <li key={i.key}>{i.label}</li>
                    ))}
                  </ul>
                  <label className="op-check">
                    <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="rm-accept" /> {v.statement}
                  </label>
                </div>
              )}
              <label className="field">
                <span>Reason (min {MIN_REASON} characters)</span>
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Operator went live under its licence; contract signed" data-testid="rm-reason" />
              </label>
              <button type="button" className="btn primary" disabled={!canAsk} onClick={() => void ask()} data-testid="rm-ask">
                Ask a second admin to approve ({pick.length} site{pick.length === 1 ? '' : 's'})
              </button>
            </>
          )}
        </section>
      )}

      {manage && real.length > 0 && (
        <section className="rm-form rm-off">
          <h4>Back to demo (immediate)</h4>
          <p className="note">Stops real money for new sessions now, without a second approval. Running sessions finish in the mode they started with.</p>
          <label className="field">
            <span>Reason (min 10 characters)</span>
            <input value={offReason} onChange={(e) => setOffReason(e.target.value)} placeholder="Operator asked to pause real money" />
          </label>
          <div className="rm-off-buttons">
            {real.map((s) => (
              <button key={s.brandId} type="button" className="btn" disabled={offReason.trim().length < 10 || busy} onClick={() => void off([s.brandId])}>
                {s.brandId} → demo
              </button>
            ))}
            <button type="button" className="btn danger" disabled={offReason.trim().length < 10 || busy} onClick={() => void off('*')}>
              All sites → demo
            </button>
          </div>
        </section>
      )}

      <h4>History</h4>
      {v.acceptances.length === 0 ? (
        <p className="empty">Real money was never switched on for this operator.</p>
      ) : (
        <ul className="rm-hist">
          {v.acceptances.map((a) => (
            <li key={a.id}>
              <strong>{fmtTime(a.createdAt)}</strong> — {a.brandIds.join(', ')}
              {a.newSites ? ' + new sites' : ''} · asked by {a.maker}, approved by {a.checker} · licence {a.licence.jurisdiction} {a.licence.number} · {a.openItems.length} accepted risk{a.openItems.length === 1 ? '' : 's'} ·{' '}
              <Link to="/audit">request {a.requestId}</Link>
              {a.endedAt ? (
                <span className="muted">
                  {' '}
                  · ended {fmtTime(a.endedAt)} by {a.endedBy}: “{a.endReason}”
                </span>
              ) : (
                <span className="tag money-real"> active</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
